import cogoToast from 'cogo-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { StatusResponse } from '../../../../../../../types/API';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { getUseAdvisoryReportQKey } from '../../../../api/getAdvisoryReport';
import {
  AdvisoryReport,
  InsurancePolicyPackage,
} from '../../../../types/AdvisoryReport';

interface Params {
  reportId: string;
  insurancePackage: InsurancePolicyPackage;
}

export const putInsurancePolicyPackageByReportId = async ({
  reportId,
  insurancePackage,
}: Params): Promise<StatusResponse> => {
  const { data, isError, error } = await wgApi.put<StatusResponse>(
    `v1/api/advisory-reports/${reportId}/policy-packages`,
    insurancePackage,
    { explicitNull: true },
  );
  if (isError) throw error;
  return data;
};

export const usePutInsurancePackage = (): UseMutationResult<
  StatusResponse,
  unknown,
  Params
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation(
    ({ reportId, insurancePackage }) =>
      putInsurancePolicyPackageByReportId({
        reportId: reportId,
        insurancePackage: insurancePackage,
      }),
    {
      onMutate: async ({ insurancePackage, reportId }) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        await queryClient.cancelQueries(qKey);

        const previousReport = queryClient.getQueryData<AdvisoryReport>(qKey);

        if (previousReport) {
          // Check if the policy is inside a package
          const prevpackages = previousReport.policy_packages || [];
          const prevPackageIndex = prevpackages?.findIndex(
            ({ id }) => id === insurancePackage?.id,
          );
          const newPackages = [...prevpackages];
          if (prevPackageIndex >= 0) {
            newPackages[prevPackageIndex] = insurancePackage;
          } else {
            newPackages.push(insurancePackage);
          }
          queryClient.setQueryData(qKey, {
            ...previousReport,
            policy_packages: newPackages,
          });

          return { qKey, previousReport };
        }
      },
      onError: (_, __, context) => {
        cogoToast.error(t('DASHBOARD_ERROR-POPUP_TITLE'));
        if (context?.previousReport) {
          queryClient.setQueryData(context.qKey, context.previousReport);
        }
      },
    },
  );
};
